import {
    BarChartRounded,
    ExpandMoreRounded,
    SubdirectoryArrowRightRounded,
} from "@mui/icons-material";
import { Box, Button, Chip, Collapse, Paper, Typography } from "@mui/material";
import React, { useState } from "react";
import {
    KboExploration,
    KboNodeFinding,
    KboNodeSelectedFinding,
    KboNodeVisual,
} from "../KboFlowDebug/KboFlowRunDebug";
import { formatNumber } from "../KboFlowDebug/KboFlowDebugUI";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import { AcxExpandableText } from "components/UI/AcxExpandableText";
import {
    ExplorationTreeNode,
    buildExplorationTree,
    cohortScopeLabel,
    formatPercent,
} from "./KboFlowRunDigestModels";
import { kboFlowRunDetailColors as pageColors } from "./KboFlowRunDetailStyles";

/** Node status chip: green when completed, red when failed, muted otherwise. */
function NodeStatusChip({ status }: { status?: string }) {
    const normalized = (status ?? "").toLowerCase();
    if (!normalized) return null;
    const failed = normalized.includes("fail");
    return (
        <Chip
            size="small"
            label={normalized}
            sx={{
                bgcolor: failed ? "#FEF3F2" : pageColors.greenSoft,
                color: failed ? "#B42318" : pageColors.green,
                fontWeight: 700,
                textTransform: "capitalize",
            }}
        />
    );
}

/** A labeled block of reasoning prose, truncated with show-more so it stays inline. */
function ReasoningBlock({
    label,
    text,
    accent,
}: {
    label: string;
    text?: string;
    accent?: boolean;
}) {
    if (!text) return null;
    return (
        <Box sx={{ mt: 1 }}>
            <Typography
                sx={{
                    color: accent ? pageColors.blue : pageColors.muted,
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                }}
            >
                {label}
            </Typography>
            <AcxExpandableText
                maxTruncatedLines={4}
                variant="body2"
                sx={{ color: pageColors.ink, lineHeight: 1.6, mt: 0.25 }}
            >
                {text}
            </AcxExpandableText>
        </Box>
    );
}

/** The share of the cohort a finding covers, e.g. "48 of 199 · 24.1%". */
function findingShareLabel(finding?: KboNodeFinding): string | undefined {
    if (!finding || typeof finding.conversationCount !== "number") {
        return undefined;
    }
    const total = finding.totalConversationsReviewed;
    const share = formatPercent(finding.conversationShare);
    const base =
        typeof total === "number"
            ? `${formatNumber(finding.conversationCount)} of ${formatNumber(total)}`
            : `${formatNumber(finding.conversationCount)} conversations`;
    return share !== "—" ? `${base} · ${share}` : base;
}

/** Button + dialog that reveals a node's pre-rendered HTML chart visuals on demand. */
function VisualsButton({ visuals }: { visuals: KboNodeVisual[] }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <Button
                size="small"
                variant="outlined"
                startIcon={<BarChartRounded />}
                onClick={() => setOpen(true)}
            >
                View charts ({visuals.length})
            </Button>
            <AcxDialog
                isOpen={open}
                onClose={() => setOpen(false)}
                title="Charts"
                maxWidth="lg"
                fullWidth
                hideCancelButton
                dialogContentChildren={
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                        }}
                    >
                        {visuals.map((visual, index) => (
                            <Box key={visual.visualId ?? index}>
                                {(visual.title || visual.caption) && (
                                    <Box sx={{ mb: 0.5 }}>
                                        {visual.title && (
                                            <Typography
                                                sx={{
                                                    color: pageColors.ink,
                                                    fontWeight: 750,
                                                }}
                                            >
                                                {visual.title}
                                            </Typography>
                                        )}
                                        {visual.caption && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {visual.caption}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                                {visual.markup ? (
                                    <Box
                                        component="iframe"
                                        title={visual.title ?? `Chart ${index + 1}`}
                                        srcDoc={visual.markup}
                                        sandbox=""
                                        sx={{
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 1,
                                            display: "block",
                                            height: 340,
                                            width: "100%",
                                        }}
                                    />
                                ) : (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        No chart markup.
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                }
            />
        </>
    );
}

/** The findings KBo considered but chose not to pursue — collapsed to avoid clutter. */
function PathsNotTaken({ items }: { items: KboNodeSelectedFinding[] }) {
    const [expanded, setExpanded] = useState(false);
    if (items.length === 0) return null;
    return (
        <Box sx={{ mt: 1.5 }}>
            <Box
                component="button"
                type="button"
                onClick={() => setExpanded((value) => !value)}
                sx={{
                    alignItems: "center",
                    bgcolor: "transparent",
                    border: 0,
                    color: pageColors.muted,
                    cursor: "pointer",
                    display: "flex",
                    font: "inherit",
                    fontSize: 12,
                    fontWeight: 800,
                    gap: 0.5,
                    letterSpacing: "0.04em",
                    p: 0,
                    textTransform: "uppercase",
                }}
            >
                <ExpandMoreRounded
                    sx={{
                        fontSize: 18,
                        transform: expanded ? "none" : "rotate(-90deg)",
                        transition: "transform 120ms",
                    }}
                />
                Paths not taken ({items.length})
            </Box>
            <Collapse in={expanded} timeout={180} mountOnEnter>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        mt: 1,
                    }}
                >
                    {items.map((item, index) => (
                        <Box
                            key={item.finding?.findingId ?? index}
                            sx={{
                                borderLeft: "3px solid",
                                borderColor: "divider",
                                pl: 1.5,
                            }}
                        >
                            <Typography
                                sx={{ color: pageColors.ink, fontWeight: 700 }}
                            >
                                {item.finding?.name ?? "Finding"}
                            </Typography>
                            {item.finding?.claim && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: pageColors.muted,
                                        lineHeight: 1.6,
                                        mt: 0.25,
                                    }}
                                >
                                    {item.finding.claim}
                                </Typography>
                            )}
                            <ReasoningBlock
                                label="Why KBo did not pursue this"
                                text={item.reason}
                            />
                        </Box>
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
}

/** A pursued finding (the path KBo took) followed by the step it led into. */
function PursuedFinding({
    selected,
    child,
    label,
}: {
    selected: KboNodeSelectedFinding;
    child?: ExplorationTreeNode;
    label: string;
}) {
    const share = findingShareLabel(selected.finding);
    return (
        <Box>
            <Paper
                variant="outlined"
                sx={{
                    borderColor: pageColors.blue,
                    borderLeftWidth: 3,
                    borderRadius: 1.5,
                    p: 1.75,
                }}
            >
                <Box
                    sx={{
                        alignItems: "baseline",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                    }}
                >
                    <Typography
                        sx={{
                            color: pageColors.ink,
                            fontSize: 15,
                            fontWeight: 800,
                        }}
                    >
                        {selected.finding?.name ?? "Finding"}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    {share && (
                        <Chip
                            size="small"
                            label={share}
                            sx={{
                                bgcolor: pageColors.blueSoft,
                                color: pageColors.blue,
                                fontWeight: 700,
                            }}
                        />
                    )}
                </Box>
                {selected.finding?.claim && (
                    <Typography
                        sx={{
                            color: pageColors.ink,
                            fontWeight: 600,
                            lineHeight: 1.55,
                            mt: 0.75,
                        }}
                    >
                        {selected.finding.claim}
                    </Typography>
                )}
                <ReasoningBlock
                    label="Evidence"
                    text={selected.finding?.reasoning}
                />
                <ReasoningBlock
                    label="Why KBo pursued this"
                    text={selected.reason}
                    accent
                />
            </Paper>
            {child && (
                <Box
                    sx={{
                        borderLeft: "2px solid",
                        borderColor: pageColors.blueSoft,
                        ml: 2,
                        mt: 1.5,
                        pl: 2,
                    }}
                >
                    <Box
                        sx={{
                            alignItems: "center",
                            color: pageColors.muted,
                            display: "flex",
                            fontSize: 13,
                            fontWeight: 700,
                            gap: 0.5,
                            mb: 1,
                        }}
                    >
                        <SubdirectoryArrowRightRounded sx={{ fontSize: 18 }} />
                        KBo explored this further
                    </Box>
                    <ExplorationStep treeNode={child} label={label} />
                </Box>
            )}
        </Box>
    );
}

/** One node in the exploration: what it analyzed, why, and the findings it fanned out. */
function ExplorationStep({
    treeNode,
    label,
}: {
    treeNode: ExplorationTreeNode;
    label: string;
}) {
    const { node, pursued, notPursued } = treeNode;
    const analysis = node.analysis;
    const visuals = analysis?.visuals ?? [];
    const failed = (node.status ?? "").toLowerCase().includes("fail");
    return (
        <Paper
            variant="outlined"
            sx={{ borderColor: "divider", borderRadius: 2, p: { xs: 2, md: 2.5 } }}
        >
            <Box
                sx={{
                    alignItems: "baseline",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                <Chip
                    size="small"
                    label={`Step ${label}`}
                    sx={{
                        bgcolor: pageColors.blueSoft,
                        color: pageColors.blue,
                        fontWeight: 800,
                    }}
                />
                <Typography
                    sx={{ color: pageColors.ink, fontSize: 17, fontWeight: 750 }}
                >
                    {cohortScopeLabel(node)}
                    {typeof analysis?.analyzedCohortCount === "number" && (
                        <Typography
                            component="span"
                            sx={{ color: pageColors.muted, fontWeight: 600 }}
                        >
                            {"  ·  "}
                            {formatNumber(analysis.analyzedCohortCount)}{" "}
                            conversations
                        </Typography>
                    )}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <NodeStatusChip status={node.status} />
                {visuals.length > 0 && <VisualsButton visuals={visuals} />}
            </Box>

            {analysis?.dimension?.alias && (
                <Typography
                    sx={{ color: pageColors.muted, fontWeight: 700, mt: 1 }}
                >
                    Split by {analysis.dimension.alias}
                </Typography>
            )}

            {analysis?.dimension?.reason && (
                <Box
                    sx={{
                        bgcolor: pageColors.blueSoft,
                        borderRadius: 1.5,
                        mt: 1,
                        p: 1.5,
                    }}
                >
                    <Typography
                        sx={{
                            color: pageColors.blue,
                            fontSize: 11,
                            fontWeight: 800,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                        }}
                    >
                        Why KBo explored this way
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: pageColors.ink, lineHeight: 1.6, mt: 0.5 }}
                    >
                        {analysis.dimension.reason}
                    </Typography>
                </Box>
            )}

            {failed && node.error && (
                <Box
                    sx={{
                        bgcolor: pageColors.orangeSoft,
                        borderRadius: 1.5,
                        mt: 1.5,
                        p: 1.5,
                    }}
                >
                    <Typography
                        sx={{
                            color: pageColors.orange,
                            fontSize: 11,
                            fontWeight: 800,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                        }}
                    >
                        This branch could not complete
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: pageColors.ink, lineHeight: 1.6, mt: 0.5 }}
                    >
                        {node.error}
                    </Typography>
                </Box>
            )}

            {pursued.length > 0 && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                        mt: 1.5,
                    }}
                >
                    {pursued.map((item, index) => (
                        <PursuedFinding
                            key={item.selected.finding?.findingId ?? index}
                            selected={item.selected}
                            child={item.child}
                            label={`${label}.${index + 1}`}
                        />
                    ))}
                </Box>
            )}

            <PathsNotTaken items={notPursued} />
        </Paper>
    );
}

/**
 * Renders the v5 exploration as a path-first fan-out: the branches KBo pursued are
 * prominent and nest into the steps they produced, while the findings it set aside stay
 * one click away. Reasoning (why each split, why each finding was pursued or not) is
 * shown inline with each step.
 */
export function ExplorationNarrative({
    exploration,
}: {
    exploration: KboExploration;
    rootQuestionId?: string;
    totalConversations?: number;
}) {
    const roots = buildExplorationTree(exploration);
    if (roots.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No exploration steps recorded.
            </Typography>
        );
    }
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {roots.map((root, index) => (
                <ExplorationStep
                    key={root.node.nodeId ?? index}
                    treeNode={root}
                    label={`${index + 1}`}
                />
            ))}
        </Box>
    );
}
